import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { PAGE_ROOT } from '../components/work/page-chrome';
import { isAuthSession, setAuthSession } from '../lib/api';
import { postLoginPath } from '../lib/project-path';
import { useSession } from '../components/session-context';
import PulseLogo from '../components/PulseLogo';

export default function GoogleComplete() {
  const navigate = useNavigate();
  const { ready, email, starredProjectId, reload } = useSession();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function complete(): Promise<void> {
      try {
        const res = await fetch('/api/auth/google/complete', { method: 'POST' });
        if (!res.ok) {
          throw new Error('Google sign-in is not complete.');
        }
        const body: unknown = await res.json();
        if (!isAuthSession(body)) {
          throw new Error('Google sign-in did not return session tokens.');
        }
        setAuthSession(body);
        const session = await reload();
        if (!cancelled) navigate(postLoginPath(session.starredProjectId), { replace: true });
      } catch {
        if (!cancelled) setError('Use a verified @bright.blue Google account.');
      }
    }
    void complete();
    return () => {
      cancelled = true;
    };
  }, [navigate, reload]);

  if (ready && email && !error) {
    return <Navigate to={postLoginPath(starredProjectId)} replace />;
  }

  return (
    <div className={PAGE_ROOT}>
      <main className="flex justify-center px-4 py-16">
        <Card className="w-full max-w-sm">
          <CardHeader className="items-center text-center">
            <PulseLogo size="md" />
            <CardTitle>Signing you in</CardTitle>
            <CardDescription>Finishing Google sign-in.</CardDescription>
          </CardHeader>
          <CardContent>
            {error ? (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : (
              <p className="text-sm text-muted-foreground">Please wait.</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
